import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store/store';
import { API_ENDPOINTS } from '../api/endpoints';

const POLL_INTERVAL_MS = 2_000;
// TD-2263 (Item 5d): raise the ceiling. Bulk Surfe batches over many contacts
// can legitimately run past 60s; the old 30-attempt cap surfaced a spurious
// "failed" toast for jobs that were still working. 90 × 2s = 180s.
const DEFAULT_POLL_MAX_ATTEMPTS = 90;
// TD-2263 (Item 5c): a single transient status-endpoint network error (gateway
// blip, brief 5xx) used to be treated as terminal failure. Retry a few times
// with a short linear backoff before declaring the job failed.
const DEFAULT_MAX_ERROR_RETRIES = 3;
const ERROR_RETRY_BASE_MS = 1_000;

/**
 * Polls `GET /enrichment/job/{jobId}/status` every 2s until the job reaches a
 * terminal state or the poll ceiling is reached.
 *
 * BE response shape (controllers/enrichment.js getJobStatus, verified
 * 2026-06-09):
 *   { jobId, status, totalContacts, enrichedCount, creditsDeducted, completedAt }
 *   BE status ∈ 'pending' | 'completed' | 'failed'
 *     - 'completed' is set by the Surfe batch-completion webhook; a completed
 *       job with enrichedCount === 0 means Surfe found nothing for the
 *       submitted contacts (NOT an error — see KeyContactsTab Item 5a).
 *     - 'failed' is set by markFailed() when the Surfe call (or a post-INSERT
 *       step) throws — a genuine provider/system failure.
 *
 * Returned status adds a FE-only 'timeout' state (TD-2263 Item 5b): the poll
 * ceiling was reached while the job was still 'pending'. This is distinct from
 * a BE 'failed' so the caller can word the message accurately ("still
 * processing, check back" vs "the job failed").
 *
 * When `jobId` is null/undefined the hook is inert (idle, no polling).
 *
 * @param {string|null} jobId
 * @param {Object} [options]
 * @param {number} [options.maxAttempts=90] - poll ceiling (× POLL_INTERVAL_MS)
 * @param {number} [options.maxErrorRetries=3] - transient-error retries before
 *   declaring failure
 * @returns {{
 *   status: 'idle'|'pending'|'completed'|'failed'|'timeout',
 *   job: object|null
 * }}
 */
const useEnrichmentJobPolling = (
  jobId,
  { maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS, maxErrorRetries = DEFAULT_MAX_ERROR_RETRIES } = {}
) => {
  const store = useStore();
  const [status, setStatus] = useState('idle');
  const [job, setJob] = useState(null);

  // Refs let the polling loop read the latest config/state without re-creating
  // the effect on every state change. Without this, advancing the timer would
  // race with React's commit and produce extra requests.
  const isMounted = useRef(true);
  const attemptsRef = useRef(0);
  const errorRetriesRef = useRef(0);
  const timerRef = useRef(null);
  const maxAttemptsRef = useRef(maxAttempts);
  maxAttemptsRef.current = maxAttempts;
  const maxErrorRetriesRef = useRef(maxErrorRetries);
  maxErrorRetriesRef.current = maxErrorRetries;

  const fetchOnce = useCallback(() => {
    if (!isMounted.current || !jobId) return;
    attemptsRef.current += 1;
    store.makeRequest({
      endpoint: {
        ...API_ENDPOINTS.ENRICHMENT_JOB_STATUS,
        path: `/enrichment/job/${jobId}/status`
      },
      onSuccess: (data) => {
        if (!isMounted.current) return;
        // A successful poll clears the transient-error budget — only
        // consecutive errors should count toward giving up.
        errorRetriesRef.current = 0;
        const next = data?.status;
        setJob(data ?? null);
        if (next === 'completed' || next === 'failed') {
          setStatus(next);
          return;
        }
        // pending → schedule next tick unless we've hit the ceiling, in which
        // case surface 'timeout' (the job may still be running on the BE).
        if (attemptsRef.current >= maxAttemptsRef.current) {
          setStatus('timeout');
          return;
        }
        setStatus('pending');
        timerRef.current = setTimeout(fetchOnce, POLL_INTERVAL_MS);
      },
      onError: () => {
        if (!isMounted.current) return;
        errorRetriesRef.current += 1;
        // Retry a few transient errors with a short linear backoff before
        // declaring the job failed. A persistent error still terminates.
        if (errorRetriesRef.current <= maxErrorRetriesRef.current) {
          setStatus('pending');
          timerRef.current = setTimeout(
            fetchOnce,
            ERROR_RETRY_BASE_MS * errorRetriesRef.current
          );
          return;
        }
        setStatus('failed');
      }
    });
  }, [jobId, store]);

  useEffect(() => {
    isMounted.current = true;
    attemptsRef.current = 0;
    errorRetriesRef.current = 0;
    if (!jobId) {
      setStatus('idle');
      setJob(null);
      return undefined;
    }
    fetchOnce();
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobId, fetchOnce]);

  return { status, job };
};

export default useEnrichmentJobPolling;
